import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  getAllCandidates,
  myInterviews,
  createRomm,
} from "../feature/interview/interviewAPI";
import { useDispatch, useSelector } from "react-redux";
import Loader from "../Components/loader";
import ShareInvitation from "../Components/ShareInvitation";
import Calendar from "../Components/Calendar";
import Badge from "../Components/ui/Badge";
import Avatar from "../Components/ui/Avatar";
import {
  Plus,
  Video,
  CalendarClock,
  Users,
  Clock,
  ChevronLeft,
  ChevronRight,
  Inbox,
  Search,
  X,
  CalendarCheck2,
  CheckCircle2,
  CalendarOff,
  ArrowUpRight,
} from "lucide-react";

const Dashboard = ({ interviewFinsished }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const candidates = useSelector((state) => state.interview.candidates.data);
  const interviews = useSelector((state) => state.interview.interviews.data);
  const candidatesLoading = useSelector(
    (state) => state.interview.candidates.loading,
  );
  const interviewsLoading = useSelector(
    (state) => state.interview.interviews.loading,
  );
  const candidatesError = useSelector(
    (state) => state.interview.candidates.error,
  );
  const interviewsError = useSelector(
    (state) => state.interview.interviews.error,
  );
  const loading = candidatesLoading || interviewsLoading;
  const error = candidatesError || interviewsError;
  const [user, setUser] = useState();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [searchEmail, setSearchEmail] = useState("");
  const [searchResult, setSearchResult] = useState(null);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [scheduledAt, setScheduledAt] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
  }, []);

  useEffect(() => {
    document.title = "Interviewly - Dashboard";
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (user?.role === "interviewer") {
        await dispatch(getAllCandidates());
      }

      const interviewsResult = await dispatch(myInterviews());

      if (interviewsResult.error?.message === "Authentication required.") {
        localStorage.removeItem("user");
        navigate("/login");
      }
    };

    if (user) {
      fetchData();
    }
  }, [dispatch, navigate, user]);

  useEffect(() => {
    if (location.state?.interviewFinished && user) {
      dispatch(myInterviews());
      window.history.replaceState({}, document.title);
    }
  }, [location.state, dispatch, user]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getUserInfo = (userId) => {
    if (user && user._id === userId) {
      return { name: user.name, email: user.email };
    }

    if (candidates && candidates.length > 0) {
      const candidate = candidates.find((c) => c._id === userId);
      if (candidate) {
        return { name: candidate.name, email: candidate.email };
      }
    }

    return { name: null, email: null };
  };

  const getParticipantInfo = (interview) => {
    if (!user) return { name: null, email: null, role: null, id: null };

    if (user._id === interview.interviewerId) {
      const userInfo = getUserInfo(interview.candidateId);
      return {
        name: userInfo.name,
        email: userInfo.email,
        role: "Candidate",
        id: interview.candidateId,
      };
    }

    if (user._id === interview.candidateId) {
      const userInfo = getUserInfo(interview.interviewerId);
      return {
        name: userInfo.name,
        email: userInfo.email,
        role: "Interviewer",
        id: interview.interviewerId,
      };
    }

    return { name: null, email: null, role: null, id: null };
  };

  useEffect(() => {
    const permissionErrors = [
      "Only interviewer can get the information for all other candidates",
      "Only interviewer",
      "unauthorized",
    ];

    if (
      error &&
      !permissionErrors.some((permError) =>
        error.toLowerCase().includes(permError.toLowerCase()),
      )
    ) {
      alert(error);
    }
  }, [error]);

  const lastFiveCandidates =
    candidates && candidates.length > 0 ? candidates.slice(-5).reverse() : [];

  const handleSearch = (e) => {
    e.preventDefault();
    if (!searchEmail) return;
    const found = candidates.find((c) => c.email === searchEmail.trim());
    setSearchResult(found || null);
  };

  const handleSelectCandidate = (candidate) => {
    setSelectedCandidate(candidate);
  };

  const handleCreateInterview = async (e) => {
    e.preventDefault();
    if (!selectedCandidate || !scheduledAt) return;

    await dispatch(
      createRomm({
        candidateId: selectedCandidate._id,
        scheduleAt: scheduledAt,
      }),
    );

    setShowCreateModal(false);
    setSelectedCandidate(null);
    setScheduledAt("");
    setSearchEmail("");
    setSearchResult(null);

    dispatch(myInterviews());
  };

  const handleJoinInterview = (interview) => {
    if (interview.status === "completed") {
      const confirmed = window.confirm(
        "This interview has already been completed. Do you want to view it anyway?",
      );
      if (confirmed) {
        navigate(`/interview/${interview.roomId}`);
      }
    } else if (interview.status === "expired") {
      const confirmed = window.confirm(
        "This interview has expired. Do you want to view it anyway?",
      );
      if (confirmed) {
        navigate(`/interview/${interview.roomId}`);
      }
    } else {
      navigate(`/interview/${interview.roomId}`);
    }
  };

  const sortedInterviews = [...interviews].sort((a, b) => {
    const statusOrder = { scheduled: 1, ongoing: 1, expired: 2, completed: 2 };
    const aOrder = statusOrder[a.status] || 3;
    const bOrder = statusOrder[b.status] || 3;

    if (aOrder === bOrder) {
      return new Date(b.scheduledAt) - new Date(a.scheduledAt);
    }

    return aOrder - bOrder;
  });

  const totalPages = Math.ceil(sortedInterviews.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedInterviews = sortedInterviews.slice(startIndex, endIndex);

  useEffect(() => {
    setCurrentPage(1);
  }, [interviews?.length]);

  const goToPage = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const nextPage = () => goToPage(currentPage + 1);
  const prevPage = () => goToPage(currentPage - 1);

  useEffect(() => {
    const stopAllMediaStreams = () => {
      document.querySelectorAll("video").forEach((video) => {
        if (video.srcObject) {
          const stream = video.srcObject;
          stream.getTracks().forEach((track) => {
            track.stop();
          });
          video.srcObject = null;
          video.load();
        }
      });

      document.querySelectorAll("audio").forEach((audio) => {
        if (audio.srcObject) {
          const stream = audio.srcObject;
          stream.getTracks().forEach((track) => {
            track.stop();
          });
          audio.srcObject = null;
        }
      });
    };

    if (location.state?.interviewFinsished) {
      setTimeout(stopAllMediaStreams, 100);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const upcomingInterviews = (interviews || []).filter(
    (i) => i.status === "scheduled" || i.status === "ongoing",
  );

  const stats = [
    {
      label: "Total Interviews",
      value: (interviews || []).length,
      icon: Video,
      tint: "text-amber-300 bg-amber-400/10 ring-amber-400/20",
    },
    {
      label: "Upcoming",
      value: upcomingInterviews.length,
      icon: CalendarClock,
      tint: "text-sky-300 bg-sky-400/10 ring-sky-400/20",
    },
    {
      label: "Completed",
      value: (interviews || []).filter((i) => i.status === "completed").length,
      icon: CheckCircle2,
      tint: "text-emerald-300 bg-emerald-400/10 ring-emerald-400/20",
    },
    {
      label: "Expired",
      value: (interviews || []).filter((i) => i.status === "expired").length,
      icon: CalendarOff,
      tint: "text-red-300 bg-red-400/10 ring-red-400/20",
    },
  ];

  const isDimmed = (status) => status === "completed" || status === "expired";

  const closeModal = () => {
    setShowCreateModal(false);
    setSearchEmail("");
    setSearchResult(null);
    setSelectedCandidate(null);
    setScheduledAt("");
  };

  return (
    <div className="min-h-screen bg-cocoa-700">
      <div className="container-page py-8 sm:py-10">
        {/* Page header */}
        <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="mb-1 text-sm font-semibold tracking-widest uppercase text-amber-400/70">
              Dashboard
            </p>
            <h1 className="text-3xl font-bold tracking-tight text-cream-100 sm:text-4xl">
              Welcome back{user?.name ? `, ${user.name.split(" ")[0]}` : ""}
            </h1>
            <p className="mt-2 text-sm text-amber-100/50">
              Manage your interviews, schedule sessions, and track progress.
            </p>
          </div>
          {user?.role === "interviewer" && (
            <button onClick={() => setShowCreateModal(true)} className="btn btn-primary btn-md shrink-0">
              <Plus className="h-4 w-4" strokeWidth={2.25} />
              Create Interview
            </button>
          )}
        </div>

        {/* Stats */}
        <div className="mb-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
          {stats.map((stat) => {
            const Icon = stat.icon;
            return (
              <div key={stat.label} className="card flex items-center gap-4 p-5">
                <div
                  className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ring-1 ring-inset ${stat.tint}`}
                >
                  <Icon className="h-5 w-5" strokeWidth={2} />
                </div>
                <div className="min-w-0">
                  <p className="text-2xl font-bold tracking-tight text-cream-100">
                    {stat.value}
                  </p>
                  <p className="truncate text-xs font-medium text-amber-100/50">
                    {stat.label}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Calendar + Upcoming grid */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="lg:col-span-1">
            <Calendar interviews={interviews || []} />
          </div>

          <div className="lg:col-span-2">
            <div className="card flex h-full flex-col p-5 sm:p-6">
              <div className="mb-5 flex items-center justify-between gap-3">
                <h2 className="flex items-center gap-2.5 text-lg font-semibold tracking-tight text-cream-100">
                  <CalendarClock className="h-5 w-5 text-amber-400" strokeWidth={2} />
                  Upcoming Interviews
                </h2>
                <span className="badge badge-scheduled">
                  {upcomingInterviews.length} scheduled
                </span>
              </div>
              {loading ? (
                <div className="flex flex-1 items-center justify-center py-10">
                  <div className="h-8 w-8 animate-spin rounded-full border-2 border-amber-400/30 border-t-amber-400" />
                </div>
              ) : upcomingInterviews.length > 0 ? (
                <div className="flex flex-col gap-3">
                  {upcomingInterviews.slice(0, 5).map((interview) => {
                    const participantInfo = getParticipantInfo(interview);
                    return (
                      <div
                        key={interview._id}
                        className="group flex items-center gap-4 rounded-xl border border-white/10 bg-white/[0.04] p-4 transition-all duration-200 hover:border-white/20 hover:bg-white/[0.08]"
                      >
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-400/10 text-amber-300 ring-1 ring-inset ring-amber-400/20">
                          <Video className="h-5 w-5" strokeWidth={2} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="font-semibold text-cream-100">
                              {participantInfo.name || "Interview session"}
                            </p>
                            <Badge status={interview.status} />
                          </div>
                          <p className="mt-1 flex items-center gap-1.5 text-xs text-amber-100/50">
                            <Clock className="h-3.5 w-3.5" />
                            {formatDate(interview.scheduledAt)}
                            <span className="mx-1 text-white/15">·</span>
                            <span className="font-mono">{interview.roomId}</span>
                          </p>
                        </div>
                        <button
                          onClick={() => handleJoinInterview(interview)}
                          className="btn btn-secondary btn-sm shrink-0"
                        >
                          Join
                          <ArrowUpRight className="h-4 w-4" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="flex flex-1 flex-col items-center justify-center gap-3 py-12 text-center">
                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/5 text-amber-200/40 ring-1 ring-inset ring-white/10">
                    <Inbox className="h-7 w-7" />
                  </div>
                  <p className="text-sm font-medium text-amber-100/60">
                    No upcoming interviews
                  </p>
                  <p className="max-w-xs text-xs text-amber-100/40">
                    {user?.role === "interviewer"
                      ? "Schedule your first interview to get started."
                      : "You're all caught up. New invites will appear here."}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* All Interviews */}
        <div className="mt-10">
          <div className="mb-5 flex items-center justify-between gap-3">
            <h2 className="flex items-center gap-2.5 text-lg font-semibold tracking-tight text-cream-100">
              <Users className="h-5 w-5 text-amber-400" strokeWidth={2} />
              All Interviews
            </h2>
            {sortedInterviews.length > 0 && (
              <span className="text-xs font-medium text-amber-100/40">
                {sortedInterviews.length} total
              </span>
            )}
          </div>

          {loading ? (
            <Loader />
          ) : sortedInterviews.length === 0 ? (
            <div className="card flex flex-col items-center justify-center gap-3 py-16 text-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-white/5 text-amber-200/40 ring-1 ring-inset ring-white/10">
                <CalendarCheck2 className="h-8 w-8" />
              </div>
              <p className="text-lg font-semibold text-amber-100/70">
                No interviews yet
              </p>
              {user?.role === "interviewer" && (
                <>
                  <p className="max-w-sm text-sm text-amber-100/40">
                    Create your first interview to start conducting sessions
                    with candidates.
                  </p>
                  <button
                    onClick={() => setShowCreateModal(true)}
                    className="btn btn-primary btn-md mt-2"
                  >
                    <Plus className="h-4 w-4" strokeWidth={2.25} />
                    Create Interview
                  </button>
                </>
              )}
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              {paginatedInterviews.map((interview) => {
                const participantInfo = getParticipantInfo(interview);
                const dimmed = isDimmed(interview.status);
                const cardClass = dimmed
                  ? interview.status === "expired"
                    ? "border-red-400/15 bg-red-950/20"
                    : "border-white/5 bg-white/[0.02]"
                  : "border-white/10 bg-white/[0.04] hover:border-white/20 hover:bg-white/[0.07]";

                return (
                  <div
                    key={interview._id}
                    className={`card flex flex-col gap-4 rounded-2xl border p-5 transition-all duration-200 sm:flex-row sm:items-center sm:justify-between sm:p-6 ${
                      !dimmed ? "card-hover" : ""
                    } ${cardClass}`}
                  >
                    <div className="flex min-w-0 flex-1 gap-4">
                      <div
                        className={`hidden h-12 w-12 shrink-0 items-center justify-center rounded-xl ring-1 ring-inset sm:flex ${
                          dimmed
                            ? "bg-white/5 text-amber-200/40 ring-white/10"
                            : "bg-amber-400/10 text-amber-300 ring-amber-400/20"
                        }`}
                      >
                        <Video className="h-6 w-6" strokeWidth={2} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2.5">
                          <h3
                            className={`text-lg font-semibold tracking-tight ${
                              dimmed ? "text-amber-100/50" : "text-cream-100"
                            }`}
                          >
                            Interview Room
                          </h3>
                          <Badge status={interview.status} />
                        </div>

                        <p className="mt-1.5 flex items-center gap-1.5 text-xs text-amber-100/40">
                          <Video className="h-3.5 w-3.5" />
                          <span className="font-mono">Room: {interview.roomId}</span>
                        </p>

                        <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2">
                          {participantInfo.name ? (
                            <div className="flex items-center gap-2.5">
                              <Avatar name={participantInfo.name} size="sm" />
                              <div className="leading-tight">
                                <p
                                  className={`text-sm font-semibold ${
                                    dimmed ? "text-amber-100/60" : "text-amber-100"
                                  }`}
                                >
                                  {participantInfo.name}
                                </p>
                                <p className="text-xs text-amber-100/40">
                                  {participantInfo.role}
                                  {participantInfo.email
                                    ? ` · ${participantInfo.email}`
                                    : ""}
                                </p>
                              </div>
                            </div>
                          ) : participantInfo.id ? (
                            <p className="text-sm text-amber-100/60">
                              {participantInfo.role} ID: {participantInfo.id}
                            </p>
                          ) : null}

                          <div
                            className={`flex items-center gap-1.5 text-xs ${
                              dimmed ? "text-amber-100/40" : "text-amber-100/60"
                            }`}
                          >
                            <CalendarClock className="h-3.5 w-3.5" />
                            {formatDate(interview.scheduledAt)}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex shrink-0 items-center gap-2.5">
                      {interview.status === "scheduled" && (
                        <ShareInvitation
                          roomId={interview.roomId}
                          scheduledAt={interview.scheduledAt}
                        />
                      )}
                      <button
                        className={
                          interview.status === "completed"
                            ? "btn btn-sm border border-white/10 bg-white/5 text-amber-100/70 hover:bg-white/10"
                            : interview.status === "expired"
                              ? "btn btn-sm border border-red-400/20 bg-red-400/10 text-red-200 hover:bg-red-400/20"
                              : "btn btn-primary btn-sm"
                        }
                        onClick={(e) => {
                          e.stopPropagation();
                          handleJoinInterview(interview);
                        }}
                      >
                        {dimmed ? "View" : "Join"}
                        <ArrowUpRight className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                );
              })}

              {totalPages > 1 && (
                <div className="mt-2 flex flex-col items-center justify-between gap-4 rounded-2xl border border-white/10 bg-white/[0.03] p-4 sm:flex-row">
                  <p className="text-sm text-amber-100/50">
                    Showing {startIndex + 1} to{" "}
                    {Math.min(endIndex, sortedInterviews.length)} of{" "}
                    {sortedInterviews.length} interviews
                  </p>

                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={prevPage}
                      disabled={currentPage === 1}
                      className="flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 bg-white/5 text-amber-200 transition-colors hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-40"
                      aria-label="Previous page"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </button>

                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                      (page) => {
                        const showPage =
                          page === 1 ||
                          page === totalPages ||
                          (page >= currentPage - 1 && page <= currentPage + 1);
                        const showEllipsisBefore =
                          page === currentPage - 2 && currentPage > 3;
                        const showEllipsisAfter =
                          page === currentPage + 2 && currentPage < totalPages - 2;

                        if (showEllipsisBefore || showEllipsisAfter) {
                          return (
                            <span key={page} className="px-1.5 text-sm text-amber-100/40">
                              &hellip;
                            </span>
                          );
                        }

                        if (!showPage) return null;

                        return (
                          <button
                            key={page}
                            onClick={() => goToPage(page)}
                            aria-current={currentPage === page ? "page" : undefined}
                            className={`h-9 min-w-9 rounded-lg px-3 text-sm font-medium transition-colors ${
                              currentPage === page
                                ? "bg-amber-400 text-cocoa-900"
                                : "border border-white/10 bg-white/5 text-amber-200 hover:bg-white/10"
                            }`}
                          >
                            {page}
                          </button>
                        );
                      },
                    )}

                    <button
                      onClick={nextPage}
                      disabled={currentPage === totalPages}
                      className="flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 bg-white/5 text-amber-200 transition-colors hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-40"
                      aria-label="Next page"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Create Interview Modal */}
      {showCreateModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-cocoa-950/70 p-4 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-label="Create interview"
        >
          <div className="max-h-[90vh] w-full max-w-4xl animate-scale-in overflow-y-auto rounded-2xl border border-white/10 bg-cocoa-700 p-6 shadow-modal sm:p-8">
            <div className="mb-6 flex items-start justify-between gap-4">
              <div>
                <h2 className="text-2xl font-bold tracking-tight text-cream-100">
                  Create Interview
                </h2>
                <p className="mt-1 text-sm text-amber-100/50">
                  Find a candidate and schedule a session.
                </p>
              </div>
              <button
                onClick={closeModal}
                className="flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 bg-white/5 text-amber-200 transition-colors hover:bg-white/10"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <form
              className="mb-6 flex flex-col gap-2 sm:flex-row"
              onSubmit={handleSearch}
            >
              <div className="relative flex-1">
                <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-amber-200/40" />
                <input
                  type="email"
                  placeholder="Search candidate by email..."
                  value={searchEmail}
                  onChange={(e) => setSearchEmail(e.target.value)}
                  className="input pl-10"
                />
              </div>
              <div className="flex gap-2">
                <button type="submit" className="btn btn-primary btn-md flex-1 sm:flex-none">
                  <Search className="h-4 w-4" />
                  Search
                </button>
                {searchEmail && (
                  <button
                    type="button"
                    onClick={() => {
                      setSearchEmail("");
                      setSearchResult(null);
                    }}
                    className="btn btn-secondary btn-md flex-1 sm:flex-none"
                  >
                    Clear
                  </button>
                )}
              </div>
            </form>

            {!selectedCandidate ? (
              <div>
                <h3 className="mb-3 text-sm font-semibold tracking-wide uppercase text-amber-200/60">
                  {searchResult ? "Search Result" : "Recent Candidates (Last 5)"}
                </h3>
                {searchResult ? (
                  <div className="card flex flex-col gap-3 p-5 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex min-w-0 items-center gap-3">
                      <Avatar name={searchResult.name} size="md" />
                      <div className="min-w-0">
                        <p className="font-semibold text-cream-100">
                          {searchResult.name}
                        </p>
                        <p className="truncate text-sm text-amber-100/60">
                          {searchResult.email}
                        </p>
                        <p className="font-mono text-xs text-amber-100/40">
                          {searchResult._id}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleSelectCandidate(searchResult)}
                      className="btn btn-primary btn-sm shrink-0"
                    >
                      Select Candidate
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    {lastFiveCandidates.length === 0 ? (
                      <div className="card flex flex-col items-center gap-2 p-8 text-center">
                        <Inbox className="h-6 w-6 text-amber-200/40" />
                        <p className="text-sm text-amber-100/50">
                          No candidates found.
                        </p>
                      </div>
                    ) : (
                      lastFiveCandidates.map((candidate) => (
                        <div
                          key={candidate._id}
                          className="card flex items-center justify-between gap-3 p-4"
                        >
                          <div className="flex min-w-0 items-center gap-3">
                            <Avatar name={candidate.name} size="md" />
                            <div className="min-w-0">
                              <p className="truncate font-semibold text-cream-100">
                                {candidate.name}
                              </p>
                              <p className="truncate text-sm text-amber-100/60">
                                {candidate.email}
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={() => handleSelectCandidate(candidate)}
                            className="btn btn-secondary btn-sm shrink-0"
                          >
                            Select
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div>
                <h3 className="mb-3 text-sm font-semibold tracking-wide uppercase text-amber-200/60">
                  Schedule Interview
                </h3>
                <div className="card mb-5 flex items-center gap-4 p-5">
                  <Avatar name={selectedCandidate.name} size="lg" />
                  <div>
                    <p className="text-lg font-semibold text-cream-100">
                      {selectedCandidate.name}
                    </p>
                    <p className="text-sm text-amber-100/60">
                      {selectedCandidate.email}
                    </p>
                  </div>
                </div>
                <form onSubmit={handleCreateInterview}>
                  <div>
                    <label
                      htmlFor="schedule-datetime"
                      className="label"
                    >
                      Schedule Date &amp; Time
                    </label>
                    <input
                      id="schedule-datetime"
                      type="datetime-local"
                      value={scheduledAt}
                      onChange={(e) => setScheduledAt(e.target.value)}
                      min={new Date().toISOString().slice(0, 16)}
                      className="input"
                      required
                    />
                  </div>
                  <div className="mt-6 flex flex-col gap-2 sm:flex-row">
                    <button type="submit" className="btn btn-primary btn-md flex-1">
                      <CalendarClock className="h-4 w-4" />
                      Create Interview
                    </button>
                    <button
                      type="button"
                      onClick={() => setSelectedCandidate(null)}
                      className="btn btn-secondary btn-md flex-1"
                    >
                      Back to Candidates
                    </button>
                  </div>
                </form>
              </div>
            )}

            <div className="mt-6 flex justify-end">
              <button onClick={closeModal} className="btn btn-ghost btn-sm">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
